import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import contentComplianceService, {
  asAnonLimitError,
  asInsufficientCreditsError,
} from './contentComplianceService';

// Translate any caught error into a rejection payload, preserving structured
// anonymous-scan-limit / insufficient-credit info when present so the UI can
// react (rejectWithValue serializes it, so we can't pass the Error instance).
const toRejection = (error) => {
  const limitErr = asAnonLimitError(error);
  if (limitErr) {
    return {
      requiresSignup: true,
      scansUsed: limitErr.scansUsed,
      scanLimit: limitErr.scanLimit,
      retryAfterSeconds: limitErr.retryAfterSeconds,
      message: limitErr.message,
    };
  }
  const creditErr = asInsufficientCreditsError(error);
  if (creditErr) {
    return {
      requiresTopup: true,
      creditsRequired: creditErr.creditsRequired,
      creditsBalance: creditErr.creditsBalance,
      bandLabel: creditErr.bandLabel,
      message: creditErr.message,
    };
  }
  return error.message;
};

// Read/persist anonymous scans-remaining so the counter survives reloads.
const readPersistedInt = (key) => {
  const v = localStorage.getItem(key);
  return v !== null && v !== '' ? parseInt(v, 10) : null;
};

const applyAnonUsage = (state, usage) => {
  if (usage && typeof usage.remaining === 'number') {
    state.anonScansRemaining = usage.remaining;
    state.anonScanLimit = usage.limit ?? state.anonScanLimit;
    try {
      localStorage.setItem('anonScansRemaining', String(usage.remaining));
      if (usage.limit != null) localStorage.setItem('anonScanLimit', String(usage.limit));
    } catch { /* ignore quota/availability errors */ }
  }
};

const applyAnonLimitReached = (state, action) => {
  if (action.payload?.requiresSignup) {
    state.anonScansRemaining = 0;
    if (action.payload.scanLimit != null) state.anonScanLimit = action.payload.scanLimit;
    try {
      localStorage.setItem('anonScansRemaining', '0');
    } catch { /* ignore */ }
  }
};

// Helper to get the best available auth token
// Prioritizes: auth.token (Google login) > contentCompliance.authToken (anonymous) > localStorage
const getAuthTokenFromState = (getState) => {
  const state = getState();
  // First check auth slice (Google authenticated users)
  if (state.auth?.token) {
    return state.auth.token;
  }
  // Then check contentCompliance slice (anonymous users)
  if (state.contentCompliance?.authToken) {
    return state.contentCompliance.authToken;
  }
  // Finally fallback to localStorage
  return localStorage.getItem('cleanStudioToken');
};

export const checkCompliance = createAsyncThunk(
  'contentCompliance/checkCompliance',
  async (payload, { rejectWithValue, getState }) => {
    try {
      const authToken = getAuthTokenFromState(getState);
      const response = await contentComplianceService.checkCompliance(payload, authToken);
      return response;
    } catch (error) {
      return rejectWithValue(toRejection(error));
    }
  }
);

export const getJobStatus = createAsyncThunk(
  'contentCompliance/getJobStatus',
  async (jobId, { rejectWithValue, getState }) => {
    try {
      const authToken = getAuthTokenFromState(getState);
      const response = await contentComplianceService.getJobStatus(jobId, authToken);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getAuthToken = createAsyncThunk(
  'contentCompliance/getAuthToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await contentComplianceService.createToken();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const startMultipartUpload = createAsyncThunk(
  'contentCompliance/startMultipartUpload',
  async ({ fileName, fileType, fileSize }, { rejectWithValue, getState }) => {
    try {
      const authToken = getAuthTokenFromState(getState);
      const response = await contentComplianceService.startMultipartUpload(fileName, fileType, fileSize, authToken);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const generatePartUrls = createAsyncThunk(
  'contentCompliance/generatePartUrls',
  async ({ fileName, uploadId, partNumbers }, { rejectWithValue, getState }) => {
    try {
      const authToken = getAuthTokenFromState(getState);
      const response = await contentComplianceService.generatePartUrls(fileName, uploadId, partNumbers, authToken);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const uploadMultipartChunk = createAsyncThunk(
  'contentCompliance/uploadMultipartChunk',
  async ({ url, chunk, partNumber, onUploadProgress }, { rejectWithValue }) => {
    try {
      const response = await contentComplianceService.uploadMultipartChunkToPresignedUrl(url, chunk, partNumber, onUploadProgress);
      // Access ETag using a case-insensitive approach, as headers can sometimes be inconsistent
      const etag = response.headers.etag;
      if (!etag) {
        console.warn('ETag header not found in response for part', partNumber, ':', response.headers);
      }
      return { partNumber, ETag: etag };
    } catch (error) {
      return rejectWithValue({ partNumber, error: error.message });
    }
  }
);

export const completeMultipartUpload = createAsyncThunk(
  'contentCompliance/completeMultipartUpload',
  async ({ fileName, uploadId, parts, title }, { rejectWithValue, getState }) => {
    try {
      const authToken = getAuthTokenFromState(getState);
      const response = await contentComplianceService.completeMultipartUpload(fileName, uploadId, parts, authToken, title);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createJob = createAsyncThunk(
  'contentCompliance/createJob',
  async ({ fileName, platform, title }, { rejectWithValue, getState }) => {
    try {
      const authToken = getAuthTokenFromState(getState);
      const response = await contentComplianceService.createJob(fileName, platform, title, authToken);
      return response;
    } catch (error) {
      return rejectWithValue(toRejection(error));
    }
  }
);

export const listJobs = createAsyncThunk(
  'contentCompliance/listJobs',
  async ({ limit = 10, cursor = null, sortOrder = 'desc', append = false } = {}, { rejectWithValue, getState }) => {
    try {
      const authToken = getAuthTokenFromState(getState);
      console.log('[listJobs] Fetching with token:', authToken?.substring(0, 30) + '...');
      const response = await contentComplianceService.listJobs(authToken, { limit, cursor, sortOrder });
      console.log('[listJobs] Response:', response);
      // Pass append flag to reducer to know whether to replace or append jobs
      return { ...response, append };
    } catch (error) {
      console.error('[listJobs] Error:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const getGuidelines = createAsyncThunk(
  'contentCompliance/getGuidelines',
  async (platform, { rejectWithValue }) => {
    try {
      const response = await contentComplianceService.getGuidelines(platform);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateJobTitle = createAsyncThunk(
  'contentCompliance/updateJobTitle',
  async ({ jobId, title }, { rejectWithValue, getState }) => {
    try {
      const authToken = getAuthTokenFromState(getState);
      const response = await contentComplianceService.updateJobTitle(jobId, title, authToken);
      return { jobId, title, response };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Content Library thunks

export const listContent = createAsyncThunk(
  'contentCompliance/listContent',
  async ({ limit = 50, cursor = null, sortOrder = 'desc', sortBy = 'created', append = false } = {}, { rejectWithValue, getState }) => {
    try {
      const authToken = getAuthTokenFromState(getState);
      const response = await contentComplianceService.listContent(authToken, { limit, cursor, sortOrder, sortBy });
      return { ...response, append };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteContent = createAsyncThunk(
  'contentCompliance/deleteContent',
  async (contentId, { rejectWithValue, getState }) => {
    try {
      const authToken = getAuthTokenFromState(getState);
      await contentComplianceService.deleteContent(contentId, authToken);
      return contentId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getContent = createAsyncThunk(
  'contentCompliance/getContent',
  async ({ contentId }, { rejectWithValue, getState }) => {
    try {
      const authToken = getAuthTokenFromState(getState);
      return await contentComplianceService.getContent(contentId, authToken);
    } catch (error) {
      return rejectWithValue(toRejection(error));
    }
  }
);

export const scanContent = createAsyncThunk(
  'contentCompliance/scanContent',
  async ({ contentId, platform }, { rejectWithValue, getState }) => {
    try {
      const authToken = getAuthTokenFromState(getState);
      const response = await contentComplianceService.scanContent(contentId, platform, authToken);
      return response;
    } catch (error) {
      return rejectWithValue(toRejection(error));
    }
  }
);

// Dashboard thunks

export const getDashboardStats = createAsyncThunk(
  'contentCompliance/getDashboardStats',
  async (_, { rejectWithValue, getState }) => {
    try {
      const authToken = getAuthTokenFromState(getState);
      const response = await contentComplianceService.getDashboardStats(authToken);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const contentComplianceSlice = createSlice({
  name: 'contentCompliance',
  initialState: {
    message: null,
    overall_score: null,
    violations: null,
    cautions: null, // Cautions for borderline content that doesn't violate policy but warrants attention
    guideline_checks: null, // New state for guideline checks from LLM
    status: 'idle',
    error: null,
    videoUploadStatus: 'idle',
    authToken: null, // New state for authentication token
    anonScansRemaining: readPersistedInt('anonScansRemaining'), // Free scans left for anonymous users (null = unknown)
    anonScanLimit: readPersistedInt('anonScanLimit'), // Total free scans for anonymous users
    uploadId: null, // New state for multipart upload ID
    partUrls: null, // New state to store pre-signed URLs for parts
    partUploadProgress: {}, // New state to track progress of individual parts
    finalFileUrl: null, // New state to store the final S3 file URL
    jobId: null, // New state for storing the job ID
    jobsList: null, // New state for storing the list of jobs
    jobsListStatus: 'idle', // New state for tracking list jobs status
    jobsListError: null, // New state for storing list jobs error
    jobsListCursor: null, // Pagination cursor for next page
    jobsListHasMore: false, // Whether there are more jobs to load
    jobsListTotalCount: 0, // Total count of jobs for the user
    guidelines: null, // New state for platform guidelines
    guidelinesStatus: 'idle', // New state for guidelines fetch status
    // Content Library state
    contentList: null,
    contentListStatus: 'idle',
    contentListError: null,
    contentListCursor: null,
    contentListHasMore: false,
    scanStatus: 'idle',
    scanError: null,
    // Dashboard state
    dashboardStats: null,
    dashboardStatsStatus: 'idle',
    dashboardStatsError: null,
  },
  reducers: {
    clearComplianceResults: (state) => {
      state.message = null;
      state.overall_score = null;
      state.violations = null;
      state.cautions = null; // Clear cautions on clear
      state.guideline_checks = null; // Clear guideline_checks on clear
      state.status = 'idle';
      state.error = null;
      state.videoUploadStatus = 'idle';
      state.uploadId = null; // Clear uploadId on clear
      state.partUrls = null; // Clear partUrls on clear
      state.partUploadProgress = {}; // Clear partUploadProgress on clear
      state.finalFileUrl = null; // Clear finalFileUrl on clear
      state.jobId = null; // Clear jobId on clear
    },
    updateJobInList: (state, action) => {
      const { job_id, title } = action.payload;
      if (state.jobsList && Array.isArray(state.jobsList)) {
        const jobIndex = state.jobsList.findIndex(job => job.job_id === job_id);
        if (jobIndex !== -1) {
          state.jobsList[jobIndex].title = title;
        }
      }
    },
    clearScanStatus: (state) => {
      state.scanStatus = 'idle';
      state.scanError = null;
      state.message = null;
      state.overall_score = null;
      state.jobId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkCompliance.pending, (state) => {
        state.status = 'loading';
        state.message = null;
        state.overall_score = null;
        state.violations = null;
        state.cautions = null;
        state.guideline_checks = null;
        state.error = null;
      })
      .addCase(checkCompliance.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.message = action.payload.message;
        state.overall_score = action.payload.data.overall_score;
        state.violations = action.payload.data.violations;
        state.cautions = action.payload.data.cautions;
        state.guideline_checks = action.payload.data.guideline_checks;
        applyAnonUsage(state, action.payload.data.anon_usage);
      })
      .addCase(checkCompliance.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
        state.message = null;
        state.overall_score = null;
        state.violations = null;
        state.cautions = null;
        state.guideline_checks = null;
        applyAnonLimitReached(state, action);
      })
      .addCase(getAuthToken.fulfilled, (state, action) => {
        state.authToken = action.payload.token; // Assuming the token is in action.payload.token
        applyAnonUsage(state, action.payload?.data?.anon_usage);
      })
      .addCase(getAuthToken.rejected, (state, action) => {
        console.error("Failed to get auth token:", action.payload);
        state.authToken = null;
      })
      .addCase(startMultipartUpload.pending, (state) => {
        state.videoUploadStatus = 'loading';
        state.error = null;
        state.uploadId = null;
      })
      .addCase(startMultipartUpload.fulfilled, (state, action) => {
        state.videoUploadStatus = 'succeeded';
        state.uploadId = action.payload.upload_id;
      })
      .addCase(startMultipartUpload.rejected, (state, action) => {
        state.videoUploadStatus = 'failed';
        state.error = action.payload || action.error.message;
        state.uploadId = null;
      })
      .addCase(generatePartUrls.pending, (state) => {
        state.videoUploadStatus = 'loading';
        state.error = null;
        state.partUrls = null;
      })
      .addCase(generatePartUrls.fulfilled, (state, action) => {
        state.videoUploadStatus = 'succeeded';
        state.partUrls = action.payload.urls; // Assuming the URLs are in action.payload.urls
      })
      .addCase(generatePartUrls.rejected, (state, action) => {
        state.videoUploadStatus = 'failed';
        state.error = action.payload || action.error.message;
        state.partUrls = null;
      })
      .addCase(uploadMultipartChunk.pending, (state, action) => {
        state.partUploadProgress[action.meta.arg.partNumber] = { status: 'loading', progress: 0, error: null };
      })
      .addCase(uploadMultipartChunk.fulfilled, (state, action) => {
        state.partUploadProgress[action.payload.partNumber] = { status: 'succeeded', progress: 100, ETag: action.payload.ETag, error: null };
      })
      .addCase(uploadMultipartChunk.rejected, (state, action) => {
        state.partUploadProgress[action.payload.partNumber] = { status: 'failed', progress: 0, error: action.payload.error };
      })
      .addCase(completeMultipartUpload.pending, (state) => {
        state.videoUploadStatus = 'loading';
        state.error = null;
        state.finalFileUrl = null;
      })
      .addCase(completeMultipartUpload.fulfilled, (state, action) => {
        state.videoUploadStatus = 'succeeded';
        state.finalFileUrl = action.payload.data.file_url; // Assuming the final URL is in action.payload.data.file_url
      })
      .addCase(completeMultipartUpload.rejected, (state, action) => {
        state.videoUploadStatus = 'failed';
        state.error = action.payload || action.error.message;
        state.finalFileUrl = null;
      })
      .addCase(createJob.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createJob.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.jobId = action.payload.data.job_id; // Store the job ID
        applyAnonUsage(state, action.payload.data.anon_usage);
        console.log('Job created successfully:', action.payload);
      })
      .addCase(createJob.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
        applyAnonLimitReached(state, action);
        console.error('Failed to create job:', action.payload);
      })
      .addCase(getJobStatus.pending, (state) => {
        state.status = 'loading';
        // Don't clear existing data during pending - prevents UI flicker during polling
        state.error = null;
      })
      .addCase(getJobStatus.fulfilled, (state, action) => {
        const jobStatus = action.payload.data.status;
        state.message = jobStatus;
        state.overall_score = action.payload.data?.data?.overall_score;
        state.violations = action.payload.data?.data?.violations;
        state.cautions = action.payload.data?.data?.cautions;
        state.guideline_checks = action.payload.data?.data?.guideline_checks;

        // Handle FAILED status from job
        if (jobStatus === 'FAILED') {
          state.status = 'failed';
          // Extract error message from job error
          const jobError = action.payload.data?.error;
          if (jobError) {
            // Try to extract a user-friendly error message
            let errorMsg = 'Job processing failed. Please try again.';

            if (typeof jobError === 'string') {
              // Try to parse if it's a JSON string
              try {
                const parsed = JSON.parse(jobError);
                errorMsg = parsed.errorMessage || parsed.Error || jobError;
              } catch {
                errorMsg = jobError;
              }
            } else if (typeof jobError === 'object') {
              // Handle nested error structures from Step Functions
              if (jobError.Cause) {
                try {
                  const causeObj = typeof jobError.Cause === 'string'
                    ? JSON.parse(jobError.Cause)
                    : jobError.Cause;
                  errorMsg = causeObj.errorMessage || causeObj.Error || errorMsg;
                } catch {
                  errorMsg = jobError.Error || errorMsg;
                }
              } else if (jobError.errorMessage) {
                errorMsg = jobError.errorMessage;
              } else if (jobError.Error) {
                errorMsg = jobError.Error;
              } else if (jobError.message) {
                errorMsg = jobError.message;
              }
            }

            state.error = errorMsg;
          } else {
            state.error = 'Job processing failed. Please try again.';
          }
        } else {
          state.status = 'succeeded';
          state.error = null;
        }
      })
      .addCase(getJobStatus.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
        state.message = null;
        state.overall_score = null;
        state.violations = null;
        state.cautions = null;
        state.guideline_checks = null;
      })
      .addCase(getGuidelines.pending, (state) => {
        state.guidelinesStatus = 'loading';
      })
      .addCase(getGuidelines.fulfilled, (state, action) => {
        state.guidelinesStatus = 'succeeded';
        state.guidelines = action.payload.data;
      })
      .addCase(getGuidelines.rejected, (state, action) => {
        state.guidelinesStatus = 'failed';
        state.guidelines = null;
      })
      .addCase(listJobs.pending, (state) => {
        state.jobsListStatus = 'loading';
        state.jobsListError = null;
      })
      .addCase(listJobs.fulfilled, (state, action) => {
        state.jobsListStatus = 'succeeded';
        const { data, append } = action.payload;
        const jobs = data?.jobs || [];

        // If append mode, add to existing list; otherwise replace
        if (append && state.jobsList) {
          state.jobsList = [...state.jobsList, ...jobs];
        } else {
          state.jobsList = jobs;
        }

        // Store pagination state
        state.jobsListCursor = data?.next_cursor || null;
        state.jobsListHasMore = data?.has_more || false;
        state.jobsListTotalCount = data?.total_count || 0;
      })
      .addCase(listJobs.rejected, (state, action) => {
        state.jobsListStatus = 'failed';
        state.jobsListError = action.payload || action.error.message;
      })
      .addCase(updateJobTitle.fulfilled, (state, action) => {
        const { jobId, title } = action.payload;
        if (state.jobsList && Array.isArray(state.jobsList)) {
          const jobIndex = state.jobsList.findIndex(job => job.job_id === jobId);
          if (jobIndex !== -1) {
            state.jobsList[jobIndex].title = title;
          }
        }
      })
      // Content Library reducers
      .addCase(listContent.pending, (state) => {
        state.contentListStatus = 'loading';
        state.contentListError = null;
      })
      .addCase(listContent.fulfilled, (state, action) => {
        state.contentListStatus = 'succeeded';
        const { data, append } = action.payload;
        const content = data?.content || [];

        if (append && state.contentList) {
          state.contentList = [...state.contentList, ...content];
        } else {
          state.contentList = content;
        }

        state.contentListCursor = data?.next_cursor || null;
        state.contentListHasMore = data?.has_more || false;
      })
      .addCase(listContent.rejected, (state, action) => {
        state.contentListStatus = 'failed';
        state.contentListError = action.payload || action.error.message;
      })
      .addCase(deleteContent.fulfilled, (state, action) => {
        const contentId = action.payload;
        if (state.contentList && Array.isArray(state.contentList)) {
          state.contentList = state.contentList.filter(item => item.id !== contentId);
        }
      })
      .addCase(scanContent.pending, (state) => {
        state.scanStatus = 'loading';
        state.scanError = null;
      })
      .addCase(scanContent.fulfilled, (state, action) => {
        state.scanStatus = 'succeeded';
        state.jobId = action.payload.data?.job_id;
        applyAnonUsage(state, action.payload.data?.anon_usage);
      })
      .addCase(scanContent.rejected, (state, action) => {
        state.scanStatus = 'failed';
        state.scanError = action.payload || action.error.message;
        applyAnonLimitReached(state, action);
      })
      // Dashboard stats
      .addCase(getDashboardStats.pending, (state) => {
        state.dashboardStatsStatus = 'loading';
        state.dashboardStatsError = null;
      })
      .addCase(getDashboardStats.fulfilled, (state, action) => {
        state.dashboardStatsStatus = 'succeeded';
        state.dashboardStats = action.payload.data;
      })
      .addCase(getDashboardStats.rejected, (state, action) => {
        state.dashboardStatsStatus = 'failed';
        state.dashboardStatsError = action.payload || action.error.message;
      });
  },
});

export const { clearComplianceResults, updateJobInList, clearScanStatus } = contentComplianceSlice.actions;

export default contentComplianceSlice.reducer;
